import { useCallback, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import moment from 'moment';
import 'moment-timezone';
import isNumber from 'lodash/isNumber';

import { ReactComponent as Calendar } from '../../assets/img/icons/datepicker/Calendar.svg';
import { ReactComponent as Clock } from '../../assets/img/icons/datepicker/Clock.svg';
import { intervalsDate } from '../../constants/analytics.constants';
import { getUTCNow, getUTCTimestamp } from '../../utils/commonMethods';
import {
  changeSearchDateString,
  checkSearchParameter,
  checkSearchString,
  deleteSearchParameters,
  getSearchParameter,
  hasTodayQueryParam,
  setSearchParameters,
} from '../../utils/urlFunctions';
import { CustomCalendar } from './Common/CustomCalendar';
import { CustomDateArrow } from './Common/CustomDateArrow';
import { CustomIntervalSelect } from './Common/CustomIntervalSelect';
import { getTodayStartEndDates } from '../../utils/time';
import './DateTimepicker.scss';

export const DateTimepicker = ({
  history = null,
  selectMax = intervalsDate.length,
  selectMin = 1,
  calendarMax = null,
  addNotification = null,
  value,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  onChange,
  end,
  timeInterval,
  defaultValue = null,
  changedSearch,
  setChangedSearch,
  getCustomDisplayFormat,
  getCustomSelectDates,
  specificTime,
  months,
  disableDay,
  closedDays,
  singleDay,
  applyOnSelect,
  displayTimeControls,
  displayClearOption = true,
  hideArrows,
  preventOpenOptionsSelect,
  className,
}) => {
  const todayQueryParam = getSearchParameter('today');
  const [isOpen, setOpen] = useState(false);
  const [selectOpened, setSelectOpened] = useState(false);
  const [dateIcon, setDateIcon] = useState('');
  const endDateRef = useRef(null);
  const specificTimeStart = specificTime?.startTimeInterval;
  const specificTimeEnd = specificTime?.endTimeInterval;
  endDateRef.current = end;
  
  const timeTo12HFormat = time => {
    return moment(time, 'HH:mm').format('h:mm A');
  };

  const setTodayDates = useCallback(() => {
    const { startDate, endDate } = getTodayStartEndDates(timezone);
    const prevEndDate = endDateRef.current;
    
    if (!isNumber(prevEndDate) || Math.abs(endDate - prevEndDate) >= 1000) {
      const diff = endDate - startDate;
      onChange(diff / 1000, endDate);
    }

    if (history !== null && !hasTodayQueryParam()) {
      const newParams = setSearchParameters(['today'], [true]);
      history.push(newParams);
    }
  }, [onChange, timezone, history]);

  useEffect(() => {
    let startDate = getSearchParameter('startDate');
    let endDate = getSearchParameter('endDate');
    let interval = getSearchParameter('timeInterval');
    let today = getSearchParameter('today');
    if (changedSearch) {
      if (
        [startDate, endDate, interval, today].every((it) => it === null) &&
        value !== 'select' &&
        Date.now() - 100 < (end !== null && end !== 'select' ? end : 0) &&
        (defaultValue === null ||
          defaultValue === 'today' ||
          intervalsDate.find((interv) => interv.icon === defaultValue)?.value !== timeInterval)
      ) {
        if (defaultValue === 'today') {
          setTodayDates();
        } else {
          onChange(
            defaultValue !== null
              ? intervalsDate.find((interv) => interv.icon === defaultValue)?.value
              : 'select',
            defaultValue !== null ? null : 'select'
          );
        }
      } else if (
        interval !== null
          ? timeInterval === null ||
            intervalsDate.find((interv) => interv.value === timeInterval)?.icon !== interval
          : startDate !== null
          ? Number(startDate) !== value
          : Number(endDate) !== end
      ) {
        checkAddressLine();
      }
      setChangedSearch(false);
    } else if (
      defaultValue !== null &&
      [startDate, interval, value, end, timeInterval].every((it) => it === null)
    ) {
      if (defaultValue === 'today') {
        setTodayDates();
      } else {
        onChange(intervalsDate.find((interv) => interv.icon === defaultValue).value, null);
      }
    }
  }, [changedSearch, setTodayDates]);

  useEffect(() => {
    if (checkSearchParameter('interval')) {
      setDateIcon(getSearchParameter('interval'));
    } else if (timeInterval !== null) {
      setDateIcon(intervalsDate.find((item) => item.value === Number(timeInterval)).icon);
    } else if (value === 'select') {
      setDateIcon('');
    } else if (todayQueryParam === 'true') {
      setDateIcon('today');
    } else {
      setDateIcon(<Calendar />);
    }
  }, [value, end, timeInterval, todayQueryParam]);

  const checkAddressLine = () => {
    if (checkSearchString() && checkSearchParameter('startDate')) {
      let startDate = Number(getSearchParameter('startDate'));
      let endDate = Number(getSearchParameter('endDate'));
      if (
        new Date(startDate) &&
        new Date(endDate) &&
        moment(startDate).isBefore(moment()) &&
        moment(endDate).isSameOrBefore(moment())
      ) {
        if (startDate !== value || endDate !== end) {
          onChange(moment(endDate).diff(moment(startDate), 'milliseconds') / 1000, endDate);
        }
      } else {
        history.push(deleteSearchParameters(['startDate', 'endDate', 'interval']));
      }
    } else if (checkSearchString() && checkSearchParameter('timeInterval')) {
      let interval = getSearchParameter('timeInterval');
      if (intervalsDate.find((interv) => interv.icon === interval)) {
        if (timeInterval !== intervalsDate.find((interv) => interv.icon === interval).value) {
          onChange(intervalsDate.find((interv) => interv.icon === interval).value, null);
        }
      } else {
        history.push(deleteSearchParameters(['timeInterval']));
      }
    } else if (hasTodayQueryParam()) {
      setTodayDates();
    }
  };

  const onChangeAction = (startDate, endDate = null, arrowInterval = null) => {
    if (startDate === 'today' && endDate === 'today') {
      setTodayDates();
    } else if (startDate !== null && endDate === null) {
      onChange(intervalsDate.find((item) => item.icon === startDate).value, endDate);
    } else if (endDate !== null && startDate !== 'select') {
      onChange(moment(endDate).diff(moment(startDate), 'milliseconds') / 1000, moment(endDate).valueOf());
    } else if (endDate === 'select') {
      onChange('select', 'select');
    }
    if (history !== null) {
      history.push(changeSearchDateString(startDate, endDate, arrowInterval, defaultValue));
    }
  };

  const getDisplayFormat = (dates, eod = false) => {
    if (getCustomDisplayFormat) {
      return getCustomDisplayFormat(dates, eod);
    } else {
      const isSameYear = dates.every((date) => new Date(date).getFullYear() === new Date().getFullYear());
  
      if (isSameYear) {
        return eod ? 'MMM D, [End of Day]' : 'MMM D, h:mm A';
      } else {
        return eod ? 'YYYY MMM D, [End of Day]' : 'YYYY MMM D, h:mm A';
      }
    }
  };

  const getSelectDates = () => {
    if ((value !== null && value !== 'select') || timeInterval !== null) {
      if (timeInterval !== null) {
        const interval = Date.now() - timeInterval * 1000;
        const startDateStr = moment(interval).tz(timezone).format(getDisplayFormat([interval]));
        const endDateStr = moment().tz(timezone).format(getDisplayFormat([interval]));
        
        return getCustomSelectDates ? getCustomSelectDates(startDateStr, endDateStr) : `${startDateStr} - ${endDateStr}`;
      } else {
        const startTime = Math.floor(value / 1000) * 1000;
        const endTime = Math.floor(end / 1000) * 1000;
        
        const endMoment = moment(end);
        const newMoment = endMoment.clone().add(1, 'ms');
        const eod = !endMoment.isSame(newMoment, 'day');
        
        const startDateStr = moment(startTime).format(getDisplayFormat([startTime]));
        const endDateStr = moment(endTime).format(getDisplayFormat([endTime, startTime], eod));
        
        return getCustomSelectDates ? getCustomSelectDates(startDateStr, endDateStr) : `${startDateStr} - ${endDateStr}`;
      }
    } else {
      return intervalsDate[0].label;
    }
  };

  return (
    <div
      className={cx('datepicker__container', {
        'datepicker__container-single-month': months === 1,
        [className]: !!className,
      })}
    >
      {!hideArrows ? (
        <CustomDateArrow
          start={value}
          end={end}
          timeInterval={intervalsDate.find((item) => item.value === Number(timeInterval || 0))}
          timezone={timezone}
          reverse={true}
          disabled={
            value === 'select' || (moment(value).isSameOrBefore(moment('2019-01-01')) && timeInterval === null)
          }
          onChange={onChangeAction}
          closedDays={closedDays}
        />
      ) : null}
      
      <div
        className={cx('datepicker__range', { active: selectOpened || isOpen })}
        id="datepicker_range"
        onClick={() => {
          if (!preventOpenOptionsSelect && (typeof dateIcon === 'string' || hasTodayQueryParam())) {
            setSelectOpened((val) => !val);
          } else {
            setOpen(true);
          }
        }}
      >
        {(!isNaN(end) && !isNaN(value)) || timeInterval !== null ? (
          <div className="datepicker__interval-short">{dateIcon}</div>
        ) : (
          ''
        )}

        <div
          className={`datepicker__interval-text${
            value === 'select' ? ' datepicker__interval-text__placeholder' : ''
          }`}
        >
          {getSelectDates()}
        </div>

        {specificTimeStart && specificTimeEnd ? (
          <div className="datepicker__specific-time-container">
            <div className="datepicker__interval-short">
              <Clock />
            </div>

            <div className="datepicker__specific-time-text">
              {`${timeTo12HFormat(specificTimeStart)} - ${timeTo12HFormat(specificTimeEnd)}`}
            </div>
          </div>
        ) : null}
      </div>
      
      {!hideArrows ? (
        <CustomDateArrow
          start={value}
          end={end}
          timeInterval={intervalsDate.find((item) => item.value === Number(timeInterval || 0))}
          timezone={timezone}
          disabled={
            value === 'select' || timeInterval !== null || getUTCTimestamp(end, timezone) >= getUTCNow() - 60000
          }
          onChange={onChangeAction}
          closedDays={closedDays}
        />
      ) : null}
      
      {selectOpened && (
        <CustomIntervalSelect
          value={value}
          timeInterval={intervalsDate.find((item) => item.value === Number(timeInterval || 0))}
          specificTime={
            specificTime?.startTimeInterval && specificTime?.endTimeInterval
              ? { start: specificTime.startTimeInterval, end: specificTime.endTimeInterval, onApply: specificTime.onApply }
              : undefined
          }
          timezone={timezone}
          selectMax={selectMax}
          selectMin={selectMin}
          onChange={(start, end) => {
            onChangeAction(start, end);
            setSelectOpened(false);
          }}
          onCancel={() => setSelectOpened(false)}
          openCalendar={() => {
            setSelectOpened(false);
            setOpen(true);
          }}
          displayClearOption={displayClearOption}
        />
      )}
      
      {isOpen && (
        <CustomCalendar
          start={value}
          end={end}
          specificTime={specificTime}
          timeInterval={intervalsDate.find((item) => item.value === Number(timeInterval || 0))}
          withTime={true}
          timezone={timezone}
          selectMax={selectMax}
          selectMin={selectMin}
          calendarMax={calendarMax}
          addNotification={addNotification}
          onChange={(start, end) => {
            onChangeAction(start, end);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
          months={months}
          disableDay={disableDay}
          singleDay={singleDay}
          applyOnSelect={applyOnSelect}
          displayTimeControls={displayTimeControls}
          displayClearOption={displayClearOption}
        />
      )}
    </div>
  );
};
